import { useCallback, useRef, useLayoutEffect } from "react";

/**
 * useEventCallback is a custom hook that returns a memoized version of the callback function
 * that is guaranteed to always have the latest value of the function passed to it.
 * This is useful for event handlers that need to access the latest state or props.
 *
 * When to use it?
 * - You need to use a callback inside useEffect, setTimeout, addEventListener, etc.
 * - But you don't want to include everything in the dependencies array
 * - And you want the callback to always have up-to-date state or props
 *
 * @see https://usehooks-ts.com/react-hook/use-event-callback
 */
function useEventCallback<Args extends unknown[], R>(fn: (...args: Args) => R): (...args: Args) => R;
function useEventCallback<Args extends unknown[], R>(
  fn: ((...args: Args) => R) | undefined
): ((...args: Args) => R) | undefined;
function useEventCallback<Args extends unknown[], R>(
  fn: ((...args: Args) => R) | undefined
): ((...args: Args) => R) | undefined {
  const ref = useRef<typeof fn>(() => {
    throw new Error("Cannot call an event handler while rendering.");
  });

  /**
   * We use useLayoutEffect here to ensure that the ref is updated before the next render.
   * This is particularly important for cases where the callback is used in event listeners or effects.
   * If we used useEffect, the ref might not be updated in time for the next event.
   */
  useLayoutEffect(() => {
    ref.current = fn;
  }, [fn]);

  return useCallback((...args: Args) => ref.current?.(...args), [ref]) as (...args: Args) => R;
}

export default useEventCallback;
